import React from 'react';
import Button from '~/components/commonUi/Button/Button';
import SelectText from '~/components/commonUi/SelectText/SelectText';
import { ALT_CANDS, FORM_FIELDS } from '~/consts/admin';
import FormRow from '../FormRow';
import { getStore } from '~/mobx/AppStore';
import { observer } from 'mobx-react';
import DynamicFormField from '../DynamicFormField';
import { SelectOption, ValidationErrors } from '~/definitions/admin';
import { isEmpty } from 'lodash';
import { ElectionAttribute } from '~/definitions/election';

interface AdminFormProps {
    isDisabled: boolean;
    electionId: string;
    sLagId: string;
    fieldOptions: Record<string, SelectOption[]>;
    electionOptions: SelectOption[];
    companyOptions: SelectOption[];
    validationErrors: ValidationErrors;
    attributes: ElectionAttribute[];
    onChangeElection: (id: string) => void;
    onChangeSLagId: (id: string) => Promise<void>;
    onInputChange: (key: string, value: string) => void;
    onSubmit: () => void;
    onOpenCopyElectionModal: () => void;
    getAttributeValue: (key: string, attributes: ElectionAttribute[]) => string;
    getAttributeValueFromOptions: (key: string, options: SelectOption[], attributes: ElectionAttribute[]) => string;
}

const AdminForm = (props: AdminFormProps) => {
    const {
        isDisabled = false,
        electionId,
        sLagId,
        fieldOptions,
        electionOptions,
        companyOptions,
        validationErrors,
        attributes,
        onChangeElection,
        onChangeSLagId,
        onInputChange,
        onSubmit,
        onOpenCopyElectionModal,
        getAttributeValue,
        getAttributeValueFromOptions,
    } = props;

    const handleFormSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        onSubmit();
    };

    const renderFormField = (fieldName: keyof typeof FORM_FIELDS) => {
        const options = fieldOptions[fieldName];
        const error = validationErrors[fieldName];
        const value = !isEmpty(options)
            ? getAttributeValueFromOptions(fieldName, options, attributes)
            : getAttributeValue(fieldName, attributes);

        return (
            <DynamicFormField
                key={fieldName}
                fieldName={fieldName}
                value={value}
                options={options}
                error={error}
                onChange={onInputChange}
                disabled={isDisabled}
            />
        );
    };

    return (
        <form onSubmit={handleFormSubmit}>
            <div className="flex flex-col lg:flex-row lg:gap-4 w-full">
                {/* LEFT SIDE */}
                <div className="w-full lg:w-8/12 flex flex-col gap-4">
                    <FormRow label={'S-Lag'} htmlFor="sLagList">
                        <SelectText
                            id="sLagList"
                            name="sLagList"
                            extendedClassName="w-full"
                            onChange={(e) => onChangeSLagId(e.target.value)}
                            value={sLagId}
                            options={companyOptions}
                            testId="s-lag-list-select-text"
                        />
                    </FormRow>
                    {(Object.keys(FORM_FIELDS) as (keyof typeof FORM_FIELDS)[]).map((fieldName) => (
                        <FormRow
                            key={fieldName}
                            label={FORM_FIELDS[fieldName].label}
                            htmlFor={fieldName}
                            required={FORM_FIELDS[fieldName].required}
                            error={validationErrors[fieldName]}
                            extendedClassName={FORM_FIELDS[fieldName].type === 'textarea' ? '!items-start' : undefined}
                        >
                            {renderFormField(fieldName)}
                        </FormRow>
                    ))}
                </div>

                {/* RIGHT SIDE */}
                <div className="lg:w-4/12 flex flex-col gap-4 mt-4 lg:mt-0 ">
                    <div className="w-full">
                        <SelectText
                            id="election"
                            name="election"
                            extendedClassName="w-full"
                            onChange={(e) => onChangeElection(e.target.value)}
                            value={electionId}
                            options={electionOptions}
                            testId="election-select-text"
                            disabled={isDisabled}
                        />
                    </div>
                    {!isDisabled && (
                        <div className="w-full">
                            <Button
                                extendedClassName="w-fit"
                                size="small"
                                btnText="Kopier valgoppsett"
                                testId="btn-copy-election"
                                onClick={onOpenCopyElectionModal}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* SUBMIT BUTTON */}
            {!isDisabled && (
                <div className="flex flex-col lg:flex-row my-4 w-full justify-end items-end">
                    <Button type="submit" extendedClassName="w-fit" size="small" btnText="Lagre" testId="btn-submit" />
                </div>
            )}
        </form>
    );
};

const AdminFormWithStore = () => {
    const { adminStore, regionStore, companyStore, generalSettingsStore } = getStore();

    const onOpenCopyElectionModal = () => {
        adminStore.setIsCopyElectionModalOpen(true);
    };

    // Mapping of field names to their option lists
    const fieldOptions: Record<string, SelectOption[]> = {
        election_number_of_alt_candidates: ALT_CANDS,
        election_model: adminStore.getSelectOptionsFromAttribute('election_model'),
        election_election_model: adminStore.getSelectOptionsFromAttribute('election_election_model'),
        election_job_position_city: adminStore.getSelectOptionsFromAttribute('election_job_position_city'),
        election_default_region: adminStore.formatSelectOptions(regionStore.regions),
    };
    const electionList = generalSettingsStore.electionList || [];
    const companyList = companyStore.companyList || [];
    const electionId = adminStore.electionToCopy?.id || '';

    return (
        <AdminForm
            isDisabled={generalSettingsStore.isSettingsDisabled}
            electionId={electionId}
            sLagId={companyStore.selectedSlagId}
            fieldOptions={fieldOptions}
            validationErrors={adminStore.validationErrors}
            attributes={adminStore.attributesState}
            electionOptions={adminStore.formatSelectOptions(electionList)}
            companyOptions={adminStore.formatSelectOptions(companyList)}
            onChangeElection={adminStore.onChangeElection}
            onChangeSLagId={adminStore.onChangeSLagId}
            onInputChange={adminStore.onInputChange}
            onSubmit={adminStore.updateElection}
            onOpenCopyElectionModal={onOpenCopyElectionModal}
            getAttributeValue={adminStore.getAttributeValue}
            getAttributeValueFromOptions={adminStore.getAttributeValueFromOptions}
        />
    );
};

export default observer(AdminFormWithStore);
